'use client';

import {useState} from 'react';
import {usePathname, useSearchParams} from 'next/navigation';

import {useCommonData} from '@udemy-v2/common-data';
import {LocalizedHtml, useI18n} from '@udemy-v2/i18n';
import {Button} from '@udemy-v2/react-core-components';
import {FooterButtons, Modal} from '@udemy-v2/react-dialog-components';
import {FormGroup, TextInput} from '@udemy-v2/react-form-components';
import {AlertBanner} from '@udemy-v2/react-notice-components';

import {buildSignupPopupUrl} from '@/app/lib/auth/signup-popup-url';

import {CourseAccessQueryParam} from './constants';
import {CourseLimitedAccess} from './course-limited-access.react-component';
import styles from './password-protected-course.module.scss';
import classNames from 'classnames';

interface CoursePasswordFormProps {
    courseTitle?: string;
    error?: string;
}

/**
 * Form component for entering a course password in a modal. Displays an optional error message and a heading.
 */
export function CoursePasswordForm({courseTitle, error}: CoursePasswordFormProps) {
    const {gettext, interpolate} = useI18n();

    const passwordLabel = courseTitle ? (
        <LocalizedHtml
            html={interpolate(
                gettext('Enter password for: <em class="title">%(title)s</em>'),
                {title: courseTitle},
                true,
            )}
            interpolate={{title: <em />}}
        />
    ) : (
        gettext('Enter password')
    );

    return (
        <form method="get">
            <h2 id="course-password-title" className={classNames('ud-heading-lg', styles['title'])}>
                {gettext('Course password')}
            </h2>
            {error && (
                <AlertBanner
                    udStyle="error"
                    title={error}
                    showCta={false}
                    className={styles['alert-banner']}
                />
            )}
            <FormGroup label={passwordLabel} className={styles['form-group']}>
                <TextInput type="password" name="password" autoComplete="current-password" />
            </FormGroup>
            <FooterButtons>
                <Button type="submit">{gettext('View course')}</Button>
            </FooterButtons>
        </form>
    );
}

interface PasswordProtectedCourseProps {
    courseTitle?: string;
    error?: string;
}

export function PasswordProtectedCourse({courseTitle, error}: PasswordProtectedCourseProps) {
    const {gettext, locale} = useI18n();
    const commonData = useCommonData();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const [isModalOpen, setIsModalOpen] = useState(!!error);

    const isLoggedIn = !!commonData?.user;

    function handleButtonClick() {
        if (!isLoggedIn) {
            const password = searchParams?.get(CourseAccessQueryParam.PASSWORD) ?? undefined;
            window.location.assign(
                buildSignupPopupUrl({
                    locale,
                    nextPath: pathname ?? '/',
                    password: password || undefined,
                }),
            );
            return;
        }
        setIsModalOpen(true);
    }

    return (
        <CourseLimitedAccess
            buttonLabel={gettext('Enter password')}
            onButtonClick={handleButtonClick}
        >
            <Modal
                title=""
                renderTitle={() => ['course-password-title', null]}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            >
                <CoursePasswordForm courseTitle={courseTitle} error={error} />
            </Modal>
        </CourseLimitedAccess>
    );
}
